import Header from '../components/Header';
import { Box } from '@mui/material';
import AppTheme from '../theme/AppTheme';
import { Helmet } from 'react-helmet-async';
import PWAInstallPrompt from '../components/PWAInstallPrompt';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

import CssBaseline from '@mui/material/CssBaseline';

// Extend the props interface to include SEO metadata
interface BasePageProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  disableCustomTheme?: boolean;
  showPWAPrompt?: boolean;
  children: React.ReactNode;
}

function App(props: BasePageProps) {
  const { 
    children, 
    title = "CribCritic AI | Smart Home Evaluation",
    description = "AI-powered property insights and evaluations to make smarter home buying decisions.",
    keywords = "home evaluation, AI property analysis, real estate assessment",
    canonicalUrl,
    showPWAPrompt = true,
    ...restProps 
  } = props;
  
  return (
    <AppTheme {...restProps} themeComponents={xThemeComponents}>
      {/* Add Helmet for SEO metadata */}
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        
        {/* Open Graph tags for social sharing */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>
      
      <CssBaseline enableColorScheme />
      <Box sx={{ 
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        borderRadius: 0,
      }}>
        <Header />
        {children}
        
        {/* PWA Install Prompt */}
        {showPWAPrompt && <PWAInstallPrompt />}
      </Box>
    </AppTheme>
  )
}

export default App
